/* eslint-disable @typescript-eslint/prefer-ts-expect-error */

/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  AirportHeader,
  AirportSectionContent,
  AirportSectionHeader,
  EditableReferencesTable,
  EmptyState
} from './airport-detail';
import {AirportInfoEditModal} from './airport-detail/airport-info-edit-modal';
import FleetInformation from './airport-detail/fleet-information';
import AirportNoteSelected from './airport-note-selected';
import Text from '@/components/react-native/text';
import TouchableOpacity from '@/components/react-native/touchable-opacity';
import View from '@/components/react-native/view';
import {REALTIME_DB} from '@/config/firebase';
import {
  updateAirportFleet,
  updateAirportMainReferences,
  updateStationInfo
} from '@/firebase/airport-references';
import {useAppSelector} from '@/hooks/redux';
import {airportNoteCategoryTitle} from '@/services/community';
import useAirportNote from '@/stores/airport-note';
import useFleet, {mapFleetName} from '@/stores/fleet';
import {ENoteCategory, ENoteTag, INoteInfo, IReference} from '@/types/airport';
import {onValue, ref} from 'firebase/database';
import moment from 'moment';
import {useCallback, useEffect, useRef, useState} from 'react';
import {ScrollView} from 'react-native';

export const AirportDetailsComponent = ({
  preview,
  showButtonToViewFullAirportInfo
}: {
  preview?: boolean;
  showButtonToViewFullAirportInfo?: boolean;
}) => {
  const airline = useAppSelector((state) => state.user.airline);
  const airportNoteInfo = useAirportNote((state) => state.airportNoteInfo);
  const fleetTypes = useFleet((state) => state.fleetTypes);

  const [airportNotes, setAirportNotes] = useState<INoteInfo[]>([]);

  const [expandedSections, setExpandedSections] = useState({
    caution: true,
    arrival: true,
    groundOps: true,
    departure: true,
    additionalInfo: true,
    references: true,
    restrictions: true,
    fleetInformation: true
  });

  const setSelectedNote = useAirportNote((state) => state.setSelectedNote);
  const setIsEditNote = useAirportNote((state) => state.setIsEditNote);
  const setSidePanelVisible = useAirportNote(
    (state) => state.setNoteSidePanelVisible
  );

  // References editing state
  const [isEditingReferences, setIsEditingReferences] = useState(false);
  const [editedReferences, setEditedReferences] = useState<IReference[]>([]);

  // Airport info editing state
  const [isEditingAirportInfo, setIsEditingAirportInfo] = useState(false);
  const [editedAirportInfo, setEditedAirportInfo] = useState({
    effective: '',
    version: '',
    published: '',
    country: ''
  });

  // Refs for scrolling
  const scrollViewRef = useRef<ScrollView>(null);
  const sectionRefs = useRef({
    caution: useRef<typeof View>(null),
    arrival: useRef<typeof View>(null),
    groundOps: useRef<typeof View>(null),
    departure: useRef<typeof View>(null),
    additionalInfo: useRef<typeof View>(null),
    references: useRef<typeof View>(null),
    restrictions: useRef<typeof View>(null),
    fleetInformation: useRef<typeof View>(null)
  });

  // Callback functions with useCallback optimization
  const toggleSection = useCallback(
    (section: keyof typeof expandedSections) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section]
      }));
    },
    []
  );

  const handleNotePress = useCallback((noteData: INoteInfo) => {
    setSelectedNote(noteData);
    setSidePanelVisible(true);
  }, []);

  const handleAddNote = useCallback(
    (category: ENoteCategory, tags: ENoteTag[]) => {
      // Create a new note template for creation
      const newNoteTemplate: INoteInfo = {
        id: 'new-note', // Temporary ID for new note
        airport: airportNoteInfo?.icao_code as string,
        category,
        tags,
        content: '', // Empty content - will be filled in the form
        summary: '',
        is_new: true,
        created_by: 'Current User', // This would come from auth
        created_at: new Date().toISOString(),
        approved_by: '',
        approved_at: '',
        comment_count: 0
      };

      setSelectedNote(newNoteTemplate);
      setSidePanelVisible(true);
    },
    []
  );

  const handleEditNote = useCallback((note: INoteInfo) => {
    setIsEditNote(true);
    setSelectedNote(note);
    setSidePanelVisible(true);
  }, []);

  // References editing handlers
  const handleEditReferences = useCallback(() => {
    const currentReferences = airportNoteInfo?.references || [];
    // If no references exist, start with one empty reference
    const initialReferences =
      currentReferences.length === 0
        ? [{header: '', content: ''}]
        : [...currentReferences];

    setEditedReferences(initialReferences);
    setIsEditingReferences(true);
  }, [airportNoteInfo?.references]);

  const handleCancelEditReferences = useCallback(() => {
    setIsEditingReferences(false);
    setEditedReferences([]);
  }, []);

  const handleSaveReferences = useCallback(async () => {
    if (!airportNoteInfo?.icao_code) return;

    try {
      // Save main airport references
      await updateAirportMainReferences(
        airportNoteInfo.icao_code,
        editedReferences
      );

      // Update the local store with the new references
      const updatedAirportInfo = {
        ...airportNoteInfo,
        references: editedReferences
      };

      // Update the store
      const {setAirportNoteInfo} = useAirportNote.getState();
      setAirportNoteInfo(updatedAirportInfo);

      setIsEditingReferences(false);
      setEditedReferences([]);
      console.log('References updated successfully');
    } catch (error) {
      console.error('Error updating references:', error);
      // TODO: Show error toast to user
    }
  }, [airportNoteInfo, editedReferences]);

  const handleReferencesChange = useCallback((newReferences: IReference[]) => {
    setEditedReferences(newReferences);
  }, []);

  const handleFleetUpdate = useCallback(
    async (fleetType: string, references: IReference[]) => {
      if (!airportNoteInfo?.icao_code) return;

      try {
        await updateAirportFleet(
          airportNoteInfo.icao_code,
          ENoteCategory.FLEET_INFORMATION,
          mapFleetName(fleetType),
          references
        );
        console.log('Fleet updated successfully:', fleetType);
      } catch (error) {
        console.error('Error updating fleet:', error);
        // TODO: Show error toast to user
      }
    },
    [airportNoteInfo]
  );

  // Airport info editing handlers
  const handleEditAirportInfo = useCallback(() => {
    setEditedAirportInfo({
      effective: moment(airportNoteInfo?.effective).format('YYYY-MM-DD'),
      version: airportNoteInfo?.version || '',
      published: moment(airportNoteInfo?.published).format('YYYY-MM-DD'),
      country: airportNoteInfo?.country || ''
    });
    setIsEditingAirportInfo(true);
  }, [airportNoteInfo]);

  const handleCancelEditAirportInfo = useCallback(() => {
    setIsEditingAirportInfo(false);
    setEditedAirportInfo({
      effective: '',
      version: '',
      published: '',
      country: ''
    });
  }, []);

  const handleSaveAirportInfo = useCallback(async () => {
    if (!airportNoteInfo?.icao_code) return;

    try {
      // Format dates to YYYY-MM-DD before saving
      const formattedInfo = {
        effective: moment(editedAirportInfo.effective).format('YYYY-MM-DD'),
        version: editedAirportInfo.version,
        published: moment(editedAirportInfo.published).format('YYYY-MM-DD'),
        country: editedAirportInfo.country,
        updated_at: moment().format('YYYY-MM-DD HH:mm:ss')
      };

      // For now, we'll just update the local state
      await updateStationInfo(airportNoteInfo.icao_code, formattedInfo);
      useAirportNote.getState().setAirportNoteInfo({
        ...airportNoteInfo,
        effective: formattedInfo.effective,
        version: formattedInfo.version,
        published: formattedInfo.published,
        country: formattedInfo.country,
        updated_at: formattedInfo.updated_at
      });

      setIsEditingAirportInfo(false);
      setEditedAirportInfo({
        effective: '',
        version: '',
        published: '',
        country: ''
      });
      console.log('Airport info updated successfully', formattedInfo);
    } catch (error) {
      console.error('Error updating airport info:', error);
      // TODO: Show error toast to user
    }
  }, [airportNoteInfo, editedAirportInfo]);

  const handleAirportInfoChange = useCallback(
    (field: string, value: string) => {
      setEditedAirportInfo((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );

  const scrollToSection = useCallback(
    (section: keyof typeof expandedSections) => {
      if (!expandedSections[section]) {
        setExpandedSections((prev) => ({
          ...prev,
          [section]: true
        }));
      }

      setTimeout(() => {
        // @ts-ignore
        sectionRefs.current[section].current?.measureLayout(
          scrollViewRef.current as any,
          (x: number, y: number) => {
            scrollViewRef.current?.scrollTo({
              y: y - 20, // Add some padding above the section
              animated: true
            });
          },
          () => console.log('Failed to measure section layout')
        );
      }, 300); // Wait for collapse animation to complete
    },
    [expandedSections]
  );

  (window as any).airportNoteScrollToSection = scrollToSection;

  // Callback functions for section toggles
  const toggleCaution = useCallback(
    () => toggleSection('caution'),
    [toggleSection]
  );
  const toggleArrival = useCallback(
    () => toggleSection('arrival'),
    [toggleSection]
  );
  const toggleGroundOps = useCallback(
    () => toggleSection('groundOps'),
    [toggleSection]
  );
  const toggleDeparture = useCallback(
    () => toggleSection('departure'),
    [toggleSection]
  );
  const toggleReferences = useCallback(
    () => toggleSection('references'),
    [toggleSection]
  );
  const toggleRestrictions = useCallback(
    () => toggleSection('restrictions'),
    [toggleSection]
  );
  const toggleFleetInformation = useCallback(
    () => toggleSection('fleetInformation'),
    [toggleSection]
  );
  // Real-time Firebase listener for airport notes
  useEffect(() => {
    if (!airportNoteInfo?.icao_code) {
      setAirportNotes([]);
      return;
    }

    const notesRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${airportNoteInfo.icao_code}`
    );

    const unsubscribe = onValue(notesRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setAirportNotes([]);
        return;
      }

      // Convert Firebase object to array of INoteInfo
      const notes: INoteInfo[] = Object.keys(data).map((key) => ({
        ...data[key],
        id: key
      }));

      setAirportNotes(notes);
    });

    return () => unsubscribe();
  }, [airportNoteInfo?.icao_code]);

  // Real-time Firebase listener for airport station data (includes fleet info)
  useEffect(() => {
    if (!airportNoteInfo?.icao_code) {
      return;
    }

    const stationRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${airportNoteInfo.icao_code}`
    );

    const unsubscribe = onValue(stationRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Update the store with the latest station data
        const {setAirportNoteInfo} = useAirportNote.getState();
        setAirportNoteInfo(data);
        console.log('Airport station data updated:', data);
      }
    });

    return () => unsubscribe();
  }, [airportNoteInfo?.icao_code]);

  // Early return for empty state
  if (!airportNoteInfo) {
    return <EmptyState />;
  }

  return (
    <>
      <View className="bg-[#010009] flex-1 relative">
        <div
          className="flex-grow overflow-auto w-full"
          style={{
            height: window.innerHeight - 108
          }}
        >
          {/* Airport Header Info */}
          <AirportHeader
            airportNoteInfo={airportNoteInfo}
            showButtonToViewFullAirportInfo={showButtonToViewFullAirportInfo}
            onEditInfo={handleEditAirportInfo}
            preview={preview}
          />
          <div className="w-full border-t-[2px] border-b-[2px] mb-[16px] border-dashed border-red-500 text-center" />

          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.RESTRICTIONS)}
            isExpanded={expandedSections.restrictions}
            onToggle={toggleRestrictions}
            textColor="#E59EDC"
            onAddNote={() => handleAddNote(ENoteCategory.RESTRICTIONS, [])}
          />
          <View
            ref={sectionRefs.current.restrictions}
            data-section="restrictions"
          >
            <AirportSectionContent
              category={ENoteCategory.RESTRICTIONS}
              isExpanded={expandedSections.restrictions}
              airportNotes={airportNotes}
              onNotePress={handleNotePress}
              onEditNote={handleEditNote}
            />
          </View>
          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.FLEET_INFORMATION)}
            isExpanded={expandedSections.fleetInformation}
            onToggle={toggleFleetInformation}
          />
          <View
            ref={sectionRefs.current.fleetInformation}
            data-section="fleetInformation"
          >
            <FleetInformation
              onFleetUpdate={handleFleetUpdate}
              fleetTypes={fleetTypes}
              preview={preview}
            />
          </View>
          {/* Cautions Section - Now collapsible with Add note capability */}
          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.CAUTION)}
            isExpanded={expandedSections.caution}
            onToggle={toggleCaution}
            textColor="#FBB26E"
            onAddNote={() => handleAddNote(ENoteCategory.CAUTION, [])}
          />
          <View ref={sectionRefs.current.caution} data-section="caution">
            <AirportSectionContent
              category={ENoteCategory.CAUTION}
              isExpanded={expandedSections.caution}
              airportNotes={airportNotes}
              onNotePress={handleNotePress}
              onEditNote={handleEditNote}
              backgroundColor="#FBB26E4D"
            />
          </View>
          <div className="w-full border-t-[2px] border-b-[2px] my-[16px] border-dashed border-red-500 text-center" />

          {/* Arrival Section */}
          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.ARRIVAL)}
            isExpanded={expandedSections.arrival}
            onToggle={toggleArrival}
            textColor="#04C0F3"
            onAddNote={() => handleAddNote(ENoteCategory.ARRIVAL, [])}
          />
          <View ref={sectionRefs.current.arrival} data-section="arrival">
            <AirportSectionContent
              category={ENoteCategory.ARRIVAL}
              isExpanded={expandedSections.arrival}
              airportNotes={airportNotes}
              onNotePress={handleNotePress}
              onEditNote={handleEditNote}
            />
          </View>

          {/* Ground Operations Section */}
          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.GROUND_OPERATIONS)}
            isExpanded={expandedSections.groundOps}
            onToggle={toggleGroundOps}
            textColor="#A2ACFF"
            onAddNote={() => handleAddNote(ENoteCategory.GROUND_OPERATIONS, [])}
          />
          <View ref={sectionRefs.current.groundOps} data-section="groundOps">
            <AirportSectionContent
              category={ENoteCategory.GROUND_OPERATIONS}
              isExpanded={expandedSections.groundOps}
              airportNotes={airportNotes}
              onNotePress={handleNotePress}
              onEditNote={handleEditNote}
            />
          </View>

          {/* Departure Section */}
          <AirportSectionHeader
            title={airportNoteCategoryTitle(ENoteCategory.DEPARTURE)}
            isExpanded={expandedSections.departure}
            onToggle={toggleDeparture}
            textColor="#5FBE7B"
            onAddNote={() => handleAddNote(ENoteCategory.DEPARTURE, [])}
          />
          <View ref={sectionRefs.current.departure} data-section="departure">
            <AirportSectionContent
              category={ENoteCategory.DEPARTURE}
              isExpanded={expandedSections.departure}
              airportNotes={airportNotes}
              onNotePress={handleNotePress}
              onEditNote={handleEditNote}
            />
          </View>

          {/* References Section - New collapsible section for airport references */}
          <AirportSectionHeader
            title="REFERENCE INFORMATION"
            isExpanded={expandedSections.references}
            onToggle={toggleReferences}
            textColor="#FAFAF9"
          />
          <View ref={sectionRefs.current.references} data-section="references">
            {expandedSections.references && (
              <View className="px-[16px] py-[16px]">
                {isEditingReferences ? (
                  <EditableReferencesTable
                    references={editedReferences}
                    isEditing={true}
                    onReferencesChange={handleReferencesChange}
                    onEditToggle={handleCancelEditReferences}
                    onSave={handleSaveReferences}
                    onCancel={handleCancelEditReferences}
                    preview={preview}
                  />
                ) : airportNoteInfo?.references &&
                  airportNoteInfo.references.length > 0 ? (
                  <EditableReferencesTable
                    references={airportNoteInfo.references}
                    isEditing={false}
                    onReferencesChange={handleReferencesChange}
                    onEditToggle={handleEditReferences}
                    onSave={handleSaveReferences}
                    onCancel={handleCancelEditReferences}
                    preview={preview}
                  />
                ) : (
                  <View>
                    <Text
                      fontFamily="Geist"
                      className="text-[#78716C] text-[14px] font-[400] mb-[16px]"
                    >
                      No reference information available.
                    </Text>
                    {!preview && (
                      <TouchableOpacity
                        className="flex-row items-center gap-[8px] px-[16px] py-[12px] bg-[rgba(112,112,112,0.2)] border border-dashed border-[#707070] rounded-[4px]"
                        onClick={handleEditReferences}
                      >
                        <Text className="text-[#38BDF8] text-[20px] font-bold leading-none">
                          +
                        </Text>
                        <Text
                          fontFamily="Geist"
                          className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px]"
                        >
                          Add References
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            )}
          </View>
          {preview && <View className="h-[240px]" />}
        </div>
        <AirportNoteSelected />
      </View>

      {/* Airport Info Edit Modal */}
      <AirportInfoEditModal
        visible={isEditingAirportInfo}
        editedInfo={editedAirportInfo}
        onInfoChange={handleAirportInfoChange}
        onSave={handleSaveAirportInfo}
        onClose={handleCancelEditAirportInfo}
      />
    </>
  );
};
